import { Room, RoomEvent, Track } from "livekit-client";

// Bridges our OpenAI Realtime audio to a Runway Characters avatar inside OUR LiveKit room. The browser holds the "agent" seat:
// PCM16 (16 kHz, same as the official LiveKit plugin) chunks go to the Runway worker as LiveKit byte streams (topic lk.audio_stream, one stream per utterance),
// interruptions use the lk.clear_buffer RPC, and the lip-synced avatar video+audio arrive as ordinary remote tracks.
const TOPIC = "lk.audio_stream";
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export class RunwayBridge {
  constructor({ onState, onError, onVideoTrack, onAudioTrack, onConnection }) {
    this.onState = onState; this.onError = onError; this.onVideoTrack = onVideoTrack; this.onAudioTrack = onAudioTrack; this.onConnection = onConnection;
    this.room = null; this.ac = null; this.proc = null; this.src = null; this.avatar = "runway-avatar-agent";
    this.ready = false; this.forwarding = false; this.writer = null; this.q = Promise.resolve(); this.closed = false; this.tracks = []; this.hasVideo = false; this.lastPlayback = null;
  }

  async start({ livekit_url, livekit_client_token, avatar_identity }, remoteStream) {
    if (avatar_identity) this.avatar = avatar_identity;
    await this._joinRoom(livekit_url, livekit_client_token);
    if (remoteStream) this.attachAudio(remoteStream);
    await this._waitVideo(60000);
  }

  _track(track, participant) {
    if (this.closed || (participant && participant.identity !== this.avatar)) return;
    if (!this.tracks.includes(track)) this.tracks.push(track);
    if (track.kind === Track.Kind.Video) {
      const first = !this.hasVideo;
      this.hasVideo = true; this.ready = true; this.onVideoTrack?.(track); this._videoResolve?.();
      if (first) this.onConnection?.("connected"); // the billing clock starts only once the avatar is actually visible
    }
    if (track.kind === Track.Kind.Audio) this.onAudioTrack?.(track);
  }

  _waitVideo(ms) {
    if (this.hasVideo) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this._videoResolve = null; reject(new Error("Avatar Runway tidak bergabung ke ruang video (timeout)")); }, ms);
      this._videoResolve = () => { clearTimeout(timer); this._videoResolve = null; resolve(); };
    });
  }

  async _joinRoom(url, token) {
    const room = new Room({ adaptiveStream: false, dynacast: false }); this.room = room;
    room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => this._track(track, participant));
    room.on(RoomEvent.Reconnecting, () => { if (!this.closed) this.onConnection?.("reconnecting"); });
    room.on(RoomEvent.Reconnected, () => { if (!this.closed) this.onConnection?.("connected"); });
    room.on(RoomEvent.Disconnected, (reason) => { if (this.closed) return; this.onConnection?.("disconnected", reason); this.onError?.("Ruang video avatar terputus"); });
    room.on(RoomEvent.ParticipantDisconnected, (p) => { if (!this.closed && p.identity === this.avatar && this.hasVideo) { this.onConnection?.("disconnected", "avatar-left"); } });
    room.on(RoomEvent.DataReceived, (payload, p) => {
      if (p?.identity !== this.avatar) return;
      try { const ev = JSON.parse(new TextDecoder().decode(payload)); if (ev?.type) this.onState?.(ev.type); } catch (e) {}
    });
    // The Runway worker reports playback via RPC (also proves our audio reached it) — surface it as state.
    try {
      room.localParticipant.registerRpcMethod("lk.playback_started", async () => { this.lastPlayback = { type: "started", at: Date.now() }; this.onState?.("speaking"); return ""; });
      room.localParticipant.registerRpcMethod("lk.playback_finished", async () => { this.lastPlayback = { type: "finished", at: Date.now() }; this.onState?.("idle"); return ""; });
    } catch (e) {}
    await room.connect(url, token, { autoSubscribe: true });
    room.remoteParticipants.forEach((p) => p.trackPublications.forEach((pub) => { if (pub.track) this._track(pub.track, p); }));
  }

  // Capture the assistant's WebRTC audio as PCM16 @ 16 kHz; chunks are only forwarded between speakStart() and speakEnd().
  attachAudio(stream) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      this.ac = new AC({ sampleRate: 16000 });
      this.src = this.ac.createMediaStreamSource(stream);
      this.proc = this.ac.createScriptProcessor(4096, 1, 1);
      this.proc.onaudioprocess = (e) => {
        if (!this.forwarding || !this.ready) return;
        const f32 = e.inputBuffer.getChannelData(0);
        const u8 = new Uint8Array(f32.length * 2); const dv = new DataView(u8.buffer);
        for (let i = 0; i < f32.length; i++) { const v = Math.max(-1, Math.min(1, f32[i])); dv.setInt16(i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true); }
        this._write(u8);
      };
      this.src.connect(this.proc); this.proc.connect(this.ac.destination); // destination keeps the node alive; gain is silent on our side
      if (this.ac.state === "suspended") this.ac.resume().catch(() => {});
    } catch (e) { this.onError?.("Tidak bisa menangkap audio asisten untuk avatar"); }
  }

  _write(bytes) {
    const rate = String(this.ac?.sampleRate || 16000);
    this.q = this.q.then(async () => {
      if (this.closed || !this.room) return;
      if (!this.writer) this.writer = await this.room.localParticipant.streamBytes({ name: `AUDIO_${uuid()}`, topic: TOPIC, destinationIdentities: [this.avatar], attributes: { sample_rate: rate, num_channels: "1" } });
      await this.writer.write(bytes);
    }).catch((e) => { console.warn("[runway] stream write failed", e?.message); this.writer = null; });
  }

  _closeWriter() {
    this.q = this.q.then(async () => { const w = this.writer; this.writer = null; if (w) await w.close(); }).catch(() => { this.writer = null; });
  }

  speakStart() { this.forwarding = true; this._closeWriter(); }
  speakEnd() { this.forwarding = false; this._closeWriter(); }
  interrupt() {
    this.forwarding = false; this._closeWriter();
    this.q = this.q.then(() => this.room?.localParticipant.performRpc({ destinationIdentity: this.avatar, method: "lk.clear_buffer", payload: "" })).catch(() => {});
  }
  listening() {}

  // Ends the Runway session from the browser: END_CALL to the worker (graceful COMPLETED), leave the room, detach every remote track.
  endSession() {
    if (this.closed) return;
    this.closed = true; this.ready = false; this.forwarding = false;
    const w = this.writer; this.writer = null; if (w) { try { w.close().catch(() => {}); } catch (e) {} }
    try { this.room?.localParticipant.publishData(new TextEncoder().encode(JSON.stringify({ type: "END_CALL" })), { reliable: true, destinationIdentities: [this.avatar] }); } catch (e) {}
    try { this.proc?.disconnect(); this.src?.disconnect(); } catch (e) {}
    try { this.ac?.close(); } catch (e) {}
    this.proc = null; this.src = null; this.ac = null;
    for (const t of this.tracks) { try { t.detach().forEach((el) => { el.srcObject = null; el.remove(); }); } catch (e) {} }
    this.tracks = [];
    const room = this.room; this.room = null;
    setTimeout(() => { try { room?.removeAllListeners(); room?.disconnect(); } catch (e) {} }, 300);
  }

  stop() { this.endSession(); }
}
